import React from 'react';
import { Stack, Typography, Grid, Paper, Box } from '@mui/material';
import { PageTitle } from 'components/shared/page_title';
import DocumentComponent from 'components/shared/document_component';
import { useLocation } from 'react-router-dom';


type NumericStringNull = number | string | null;

interface ViewSchoolFeedingProps {
    values: {
        alimentacao_af_recurso_pnae: NumericStringNull;
        alimentacao_af_recurso_estado: NumericStringNull;
        alimentacao_af_recurso_prefeitura: NumericStringNull;
        alimentacao_total_recurso_pnae: NumericStringNull;
        alimentacao_total_recurso_estado: NumericStringNull;
        alimentacao_total_recurso_prefeitura: NumericStringNull;
        alimentacao_ata_aprovacao_uuid?: string | null;
    };
}

const formatarValorParaExibicao = (valor: number | string | null | undefined) => {
    if (!valor && valor !== 0) return '';
    const numeroValor = typeof valor === 'string' ? parseFloat(valor.toString().replace(/\./g, '').replace(',', '.')) : valor;
    if (isNaN(numeroValor)) return '';
    return numeroValor.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const ViewSchoolFeeding: React.FC<ViewSchoolFeedingProps> = ({ values }) => {
    const location = useLocation();
    // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
    const match = /cifa-(\d{4})/.exec(location.pathname);
    const anoReferencia = match ? Number(match[1]) : 2026;

    // Totais calculados automaticamente
    const totalAF =
        Number(values.alimentacao_af_recurso_pnae ?? 0) +
        Number(values.alimentacao_af_recurso_estado ?? 0) +
        Number(values.alimentacao_af_recurso_prefeitura ?? 0);

    const totalGeral =
        Number(values.alimentacao_total_recurso_pnae ?? 0) +
        Number(values.alimentacao_total_recurso_estado ?? 0) +
        Number(values.alimentacao_total_recurso_prefeitura ?? 0);

    return (
        <>
            <PageTitle
                title={`Valores para Aquisição de Alimentação Escolar - Em ${anoReferencia}`}
                subtitle={`Visualização dos valores declarados para aquisição de alimentação escolar em ${anoReferencia}.`}
            />

            <Stack marginX='4rem' marginTop='1rem' spacing='2rem' flexDirection='column' maxWidth='1152px' width='100%'>

                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                        Valores Declarados para Agricultura Familiar
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Typography fontWeight={500}>Recurso do PNAE Federal (R$)</Typography>
                            <Typography>
                                R$ {formatarValorParaExibicao(values.alimentacao_af_recurso_pnae)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography fontWeight={500}>Recurso do Governo do Estado (R$)</Typography>
                            <Typography>
                                R$ {formatarValorParaExibicao(values.alimentacao_af_recurso_estado)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography fontWeight={500}>Recurso da Prefeitura (R$)</Typography>
                            <Typography>
                                R$ {formatarValorParaExibicao(values.alimentacao_af_recurso_prefeitura)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} mt={2}>
                            <Typography fontWeight={600}>
                                Total de Recursos Aplicados nas Aquisições da Agricultura Familiar:{' '}
                                <Typography component="span" color="primary" fontWeight={700}>
                                    R$ {formatarValorParaExibicao(totalAF)}
                                </Typography>
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                        Valores Totais Declarados na Alimentação Escolar
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Typography fontWeight={500}>Total de Recursos PNAE Federal (R$)</Typography>
                            <Typography>
                                R$ {formatarValorParaExibicao(values.alimentacao_total_recurso_pnae)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography fontWeight={500}>Total de Recursos do Governo do Estado (R$)</Typography>
                            <Typography>
                                R$ {formatarValorParaExibicao(values.alimentacao_total_recurso_estado)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography fontWeight={500}>Total de Recursos da Prefeitura (R$)</Typography>
                            <Typography>
                                R$ {formatarValorParaExibicao(values.alimentacao_total_recurso_prefeitura)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} mt={2}>
                            <Typography fontWeight={600}>
                                Total Geral de Recursos na Alimentação Escolar:{' '}
                                <Typography component="span" color="primary" fontWeight={700}>
                                    R$ {formatarValorParaExibicao(totalGeral)}
                                </Typography>
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" fontWeight={600} mb={2}>
                        Ata de Aprovação dos Dados de Alimentação Escolar
                    </Typography>
                    {values.alimentacao_ata_aprovacao_uuid ? (
                        <Box>
                            <DocumentComponent
                                uuid={values.alimentacao_ata_aprovacao_uuid}
                                podeEnviar={false}
                                title="Ata de Aprovação - Alimentação Escolar"
                            />
                        </Box>
                    ) : (
                        <Typography color="text.secondary">
                            Nenhuma ata de aprovação anexada.
                        </Typography>
                    )}
                </Paper>
            </Stack>
        </>
    );
};

export default ViewSchoolFeeding;
